import {
  FluidAnchorPosition,
  FluidInPageNavConfig,
  FluidSidebarConfig,
  FluidTheme,
  FluidThemePalette,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';

interface PageSidebarProps extends FluidFunctionalComponentProps {
  config: FluidSidebarConfig;
  anchor: FluidAnchorPosition.LEFT | FluidAnchorPosition.RIGHT;
  theme: FluidTheme;
  themePalette: FluidThemePalette;
}

interface PageNavProps extends FluidFunctionalComponentProps {
  config: FluidInPageNavConfig;
  anchor: FluidAnchorPosition; // Current In-Page Nav being rendered
  pageRoot: HTMLFluidPageElement;
  theme: FluidTheme;
}

interface PageSlotContainerProps extends FluidFunctionalComponentProps {
  hasSlottedElement: boolean;
  offset?: number;
  shadow?: boolean;
}

// ================================================================ //
// -- Public Functional Components

/**
 * FLUID Page Embedded Sidebar
 */
export const PageSidebar: FunctionalComponent<PageSidebarProps> = (
  props: PageSidebarProps
) => {
  const { config, anchor, theme, themePalette } = props;

  if (isNullOrUndefined(config)) return;

  return (
    <fluid-sidebar
      config={{
        theme,
        themePalette,
        overrideGlobalTheme: true,
        ...config, // Merge here to allow default "theme" to be overridden
        anchor, // Merge "anchor" here to allow modifications from "initializeInPageNavConfig()"
      }}>
      <slot name={`${anchor}-sidebar-content`} />
    </fluid-sidebar>
  );
};

/**
 * FLUID Page Embedded In-Page Nav
 */
export const PageNav: FunctionalComponent<PageNavProps> = (
  props: PageNavProps
) => {
  const { config, anchor, pageRoot, theme } = props;

  if (isNullOrUndefined(config) || config.anchor !== anchor) return;

  return (
    <fluid-in-page-nav
      class={`page-nav anchor-${anchor}`}
      pageRoot={pageRoot}
      validInstance={true}
      config={{
        theme,
        overrideGlobalTheme: true,
        ...config, // Merge here to allow default "theme" to be overridden
      }}
    />
  );
};

/**
 * FLUID Page Context Bar Container & Slot
 */
export const PageContextBarSlot: FunctionalComponent<PageSlotContainerProps> = (
  props: PageSlotContainerProps
) => {
  const { hasSlottedElement, offset } = props;

  if (!hasSlottedElement) return;
  return (
    <div class='context-bar-container' style={{ top: `${offset}px` }}>
      <slot name='page-context-bar' />
    </div>
  );
};

/**
 * FLUID Page Footer Bar Container & Slot
 */
export const PageFooterSlot: FunctionalComponent<PageSlotContainerProps> = (
  props: PageSlotContainerProps
) => {
  const { hasSlottedElement, shadow } = props;

  if (!hasSlottedElement) return;
  return (
    <div
      class={{
        'page-footer-container': true,
        'drop-shadow': shadow,
      }}>
      <slot name='page-footer' />
    </div>
  );
};
